import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Agent } from '@/data/mockAgents';
import { Archive, Clock, Copy, Edit2, Eye, History, LockKeyhole, Play } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { archiveAgent } from '@/lib/api';
import { cn } from '@/lib/utils';

interface AgentCardProps {
  agent: Agent;
  tenants?: Array<{ id: string; name: string }>;
  companies?: Array<{ id: string; name: string }>;
}

type ManagedAgent = Agent & {
  assigned_companies?: string[] | null;
  assign_to?: string | null;
  agent_category?: string | null;
  company_id?: string | null;
  enterprise_branch_access_loaded?: boolean;
  enterprise_branch_names?: string[];
  enterprise_can_duplicate?: boolean;
  enterprise_can_manage?: boolean;
  enterprise_duplicate_scope_label?: string;
  is_system?: boolean;
  source_agent_id?: string | null;
  source_agent_history?: {
    id: string;
    name: string;
    completions: number;
    avg_score: number | null;
    status: string;
  };
  tenant_id?: string | null;
};

const typeConfig = {
  patient: {
    label: '🏥 Patient',
    badge: 'Medical Simulation',
    gradient: 'from-emerald-600 to-emerald-700',
    bgLight: 'bg-emerald-50 dark:bg-emerald-900/20',
  },
  customer: {
    label: '💼 Sales',
    badge: 'Customer Simulation',
    gradient: 'from-blue-600 to-blue-700',
    bgLight: 'bg-blue-50 dark:bg-blue-900/20',
  },
  examiner: {
    label: '👨‍⚕️ Examiner',
    badge: 'OSCE Assessment',
    gradient: 'from-purple-600 to-purple-700',
    bgLight: 'bg-purple-50 dark:bg-purple-900/20',
  },
  coach: {
    label: '🎯 Coach',
    badge: 'Coaching Session',
    gradient: 'from-amber-600 to-amber-700',
    bgLight: 'bg-amber-50 dark:bg-amber-900/20',
  },
};

export function AgentCard({ agent, tenants = [], companies = [] }: AgentCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMasterTenant } = useTenant();
  const { hasEnterpriseHierarchy } = useOrganisation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const managedAgent = agent as ManagedAgent;

  // An agent is a "system agent" ONLY if is_system=true (not based on company_id)
  // This ensures company agents without company_id due to bugs are still editable
  const isSystemAgent = managedAgent.is_system === true;
  
  // Get tenant/company name for badge display
  const agentTenantId = managedAgent.tenant_id;
  const agentCompanyId = managedAgent.company_id;
  const tenantName = tenants.find(t => t.id === agentTenantId)?.name;
  const companyName = companies.find(c => c.id === agentCompanyId)?.name;
  const enterpriseBranchAccessLoaded = managedAgent.enterprise_branch_access_loaded === true;
  const enterpriseBranchNames = managedAgent.enterprise_branch_names || [];
  const branchBadgeLabel = enterpriseBranchNames.length > 1
    ? `${enterpriseBranchNames[0]} +${enterpriseBranchNames.length - 1}`
    : enterpriseBranchNames[0] || 'All branches';
  const isTenantAdmin = !!user && (user.roles.includes('admin') || user.roles.includes('company_admin')) && !isMasterTenant;
  
  const enterpriseCanManage = managedAgent.enterprise_can_manage === true;
  const canEditDelete = isMasterTenant
    || (!isSystemAgent && (!hasEnterpriseHierarchy || enterpriseCanManage));
  const canDuplicateToScope = hasEnterpriseHierarchy
    && managedAgent.enterprise_can_duplicate === true;
  const duplicateScopeLabel = managedAgent.enterprise_duplicate_scope_label
    || 'your organisation scope';
  const canDuplicate = (isTenantAdmin && isSystemAgent) || canDuplicateToScope;

  const handleDelete = async () => {
    try {
      await archiveAgent(agent.id, hasEnterpriseHierarchy);
      setShowDeleteModal(false);
      window.location.reload();
    } catch (error) {
      console.error('Delete failed:', error);
      setShowDeleteModal(false);
    }
  };

  const config = typeConfig[agent.type as keyof typeof typeConfig] || typeConfig.patient;
  const storedAgent = agent as Agent & {
    avg_score?: number | null;
    brief_description?: string | null;
    roleplay_focus?: string | null;
    estimated_duration?: string | null;
    max_session_duration_seconds?: number | null;
  };
  const cleanText = (value?: string | null) => value
    ? value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
    : '';
  const description = cleanText(
    storedAgent.brief_description
      || storedAgent.roleplay_focus
      || agent.scenario_context
      || (agent.type === 'patient' ? agent.presenting_complaint : null),
  ) || (agent.domain ? `Domain: ${agent.domain}` : '');
  const averageScore = storedAgent.avg_score ?? agent.avgScore;
  const formattedAverageScore = typeof averageScore === 'number' && Number.isFinite(averageScore)
    ? `${Math.round(averageScore)}%`
    : '—';
  const estimatedDuration = storedAgent.estimated_duration
    || (storedAgent.max_session_duration_seconds
      ? `${Math.round(storedAgent.max_session_duration_seconds / 60)}`
      : null);
  const isEditableUnitCopy = Boolean(managedAgent.source_agent_id);
  const sourceHistory = managedAgent.source_agent_history;
  const hasRetainedHistory = Boolean(
    sourceHistory
      && ((sourceHistory.completions ?? 0) > 0 || sourceHistory.avg_score !== null),
  );

  return (
    <>
      <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 group border-border/50">
        {/* Featured Thumbnail Header */}
        <div className="relative aspect-video bg-gradient-to-br from-muted to-muted/50 overflow-hidden">
          {agent.image_url ? (
            <img
              src={agent.image_url}
              alt={agent.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className={cn("absolute inset-0 flex items-center justify-center", config.bgLight)}>
              <div className="w-20 h-20 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center shadow-lg">
                <span className="text-2xl font-bold text-primary">{agent.initials}</span>
              </div>
            </div>
          )}
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          
          {/* Type Badge - Top Left */}
          <div className="absolute top-3 left-3 right-40 z-10 flex flex-wrap gap-1.5 pr-2">
            {/* System/Tenant/Company Badge - show actual names for professional display */}
            {isSystemAgent ? (
              <Badge className="max-w-full min-w-0 text-[10px] font-semibold bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg border-0">
                <span className="truncate">🌐 System</span>
              </Badge>
            ) : enterpriseBranchAccessLoaded ? (
              <Badge
                title={enterpriseBranchNames.length > 0 ? enterpriseBranchNames.join(', ') : 'Available to all branches'}
                className="max-w-full min-w-0 text-[10px] font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg border-0"
              >
                <span className="truncate">🏢 {branchBadgeLabel}</span>
              </Badge>
            ) : companyName ? (
              <Badge className="max-w-full min-w-0 text-[10px] font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg border-0">
                <span className="truncate">🏢 {companyName}</span>
              </Badge>
            ) : tenantName ? (
              <Badge className="max-w-full min-w-0 text-[10px] font-semibold bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg border-0">
                <span className="truncate">🏛️ {tenantName}</span>
              </Badge>
            ) : (
              <Badge className="max-w-full min-w-0 text-[10px] font-semibold bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-lg border-0">
                <span className="truncate">🏢 Company</span>
              </Badge>
            )}
            <Badge className={cn("max-w-full min-w-0 text-[10px] font-semibold text-white shadow-lg border-0 bg-gradient-to-r", config.gradient)}>
              <span className="truncate">{config.label}</span>
            </Badge>
            <Badge variant="secondary" className="max-w-full min-w-0 text-[10px] bg-background/80 backdrop-blur-sm shadow-lg">
              <span className="truncate">{agent.platform === 'anam' ? '🎥 Video' : '🎙️ Audio'}</span>
            </Badge>
            {hasEnterpriseHierarchy && !isSystemAgent && !canEditDelete && (
              <Badge
                variant="secondary"
                title={`Global agent — view only. Duplicate it into ${duplicateScopeLabel} to edit.`}
                className="max-w-full min-w-0 gap-1 bg-background/90 text-[10px] font-semibold text-foreground shadow-lg"
              >
                <LockKeyhole className="h-3 w-3" />
                <span className="truncate">View only</span>
              </Badge>
            )}
          </div>
          
          {/* Actions - Top Right (ALWAYS VISIBLE for better UX) */}
          <div className="absolute top-3 right-3 z-20 flex shrink-0 gap-1">
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-8 w-8 bg-background/80 backdrop-blur-sm hover:bg-background shadow-lg"
              onClick={(e) => { e.stopPropagation(); navigate(`/admin/agents/${agent.id}/view`); }}
              title="View"
            >
              <Eye className="h-3.5 w-3.5" />
            </Button>

            {canDuplicate && (
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-8 w-8 bg-background/80 backdrop-blur-sm hover:bg-background shadow-lg"
                onClick={(e) => { e.stopPropagation(); navigate(`/admin/agents/create?template=${agent.id}${user?.company_id ? `&company=${user.company_id}` : ''}`); }}
                title={canDuplicateToScope ? `Duplicate to ${duplicateScopeLabel}` : 'Duplicate'}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            )}

            {canEditDelete && (
              <>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-8 w-8 bg-background/80 backdrop-blur-sm hover:bg-background shadow-lg"
                  onClick={(e) => { e.stopPropagation(); navigate(`/admin/agents/${agent.id}/edit`); }}
                  title="Edit"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-8 w-8 bg-background/80 backdrop-blur-sm hover:bg-destructive hover:text-destructive-foreground shadow-lg"
                  onClick={(e) => { e.stopPropagation(); setShowDeleteModal(true); }}
                  title="Archive"
                >
                  <Archive className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
          
          {/* Name & Title - Bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="text-lg font-semibold text-white truncate drop-shadow-lg">{agent.name}</h3>
            <p className="text-sm text-white/80 truncate drop-shadow-md">{agent.occupation || agent.title}</p>
          </div>
        </div>
        
        {/* Card Body */}
        <CardContent className="p-4">
          {/* Description */}
          {description && (
            <p className="text-xs text-muted-foreground mb-3 line-clamp-2 leading-relaxed">
              {description}
            </p>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="text-center p-2.5 bg-muted/50 rounded-lg">
              <div className="text-lg font-bold text-foreground">{agent.completions ?? 0}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
                {isEditableUnitCopy ? 'Unit completions' : 'Completions'}
              </div>
            </div>
            <div className="text-center p-2.5 bg-muted/50 rounded-lg">
              <div className="text-lg font-bold text-primary">{formattedAverageScore}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
                {isEditableUnitCopy ? 'Unit avg score' : 'Avg Score'}
              </div>
            </div>
          </div>

          {hasRetainedHistory && sourceHistory && (
            <button
              type="button"
              onClick={() => navigate(`/admin/agents/${sourceHistory.id}/view`)}
              className="mb-4 flex w-full items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-left text-blue-950 transition-colors hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100 dark:hover:bg-blue-950/50"
              title={`View retained history for ${sourceHistory.name}`}
            >
              <History className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="min-w-0 text-xs">
                <span className="block font-semibold">
                  {sourceHistory.completions ?? 0} historical completion{sourceHistory.completions === 1 ? '' : 's'} retained
                </span>
                <span className="block text-blue-800 dark:text-blue-200">
                  On the archived original · View history
                </span>
              </span>
            </button>
          )}
          
          {/* Metadata Tags */}
          <div className="flex flex-wrap gap-1.5 mb-4 min-h-[28px]">
            {enterpriseBranchAccessLoaded && enterpriseBranchNames.map((branchName) => (
              <Badge key={branchName} variant="outline" className="text-[10px] font-normal py-0.5">
                {branchName}
              </Badge>
            ))}
            {enterpriseBranchAccessLoaded && enterpriseBranchNames.length === 0 && (
              <Badge variant="outline" className="text-[10px] font-normal py-0.5">
                All branches
              </Badge>
            )}
            {estimatedDuration && (
              <Badge variant="outline" className="text-[10px] font-normal py-0.5 gap-1">
                <Clock className="h-3 w-3" />
                Estimated {estimatedDuration} min
              </Badge>
            )}
            {managedAgent.agent_category && (
              <Badge variant="secondary" className="text-[10px] font-normal py-0.5">
                {managedAgent.agent_category}
              </Badge>
            )}
            {agent.specialty && (
              <Badge variant="outline" className="text-[10px] font-normal py-0.5">{agent.specialty}</Badge>
            )}
            {agent.experience && (
              <Badge variant="outline" className="text-[10px] font-normal py-0.5">{agent.experience}y exp</Badge>
            )}
            {agent.age && (
              <Badge variant="outline" className="text-[10px] font-normal py-0.5">{agent.age} years</Badge>
            )}
            {agent.bmi && (
              <Badge variant="outline" className="text-[10px] font-normal py-0.5">BMI: {agent.bmi}</Badge>
            )}
          </div>
          
          {/* Assigned Tenants/Companies — show for system agents with specific assignments */}
          {isSystemAgent && managedAgent.assigned_companies && managedAgent.assigned_companies.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              <span className="text-[9px] text-muted-foreground uppercase tracking-wider font-medium w-full mb-0.5">Assigned to:</span>
              {managedAgent.assigned_companies.map((id: string) => {
                const tenantMatch = tenants.find(t => t.id === id);
                const companyMatch = companies.find(c => c.id === id);
                const name = tenantMatch?.name || companyMatch?.name || id.slice(0, 8);
                const isTenant = !!tenantMatch;
                return (
                  <Badge
                    key={id}
                    variant="outline"
                    className={cn(
                      "text-[9px] font-normal py-0 px-1.5",
                      isTenant ? "border-purple-300 text-purple-700 dark:border-purple-700 dark:text-purple-300" : ""
                    )}
                  >
                    {isTenant ? '🏢' : ''} {name}
                  </Badge>
                );
              })}
            </div>
          )}
          {isSystemAgent && (!managedAgent.assigned_companies || managedAgent.assigned_companies.length === 0) && managedAgent.assign_to === 'all' && (
            <div className="flex flex-wrap gap-1 mb-3">
              <Badge variant="outline" className="text-[9px] font-normal py-0 px-1.5 border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300">
                🌐 All Tenants &amp; Companies
              </Badge>
            </div>
          )}

          {/* Status & CTA */}
          <div className="flex items-center justify-between">
            <Badge 
              variant={agent.status === 'active' ? 'default' : 'secondary'} 
              className={cn(
                "text-[10px] font-medium",
                agent.status === 'active' && "bg-success/10 text-success hover:bg-success/20"
              )}
            >
              <span className={cn(
                "w-1.5 h-1.5 rounded-full mr-1.5",
                agent.status === 'active' ? 'bg-success' : 'bg-muted-foreground'
              )} />
              {agent.status === 'active' ? 'Active' : 'Inactive'}
            </Badge>
            <Button 
              size="sm" 
              variant="default" 
              className="text-xs h-8 gap-1.5"
              onClick={() => navigate(`/simulation/${agent.id}`)}
            >
              <Play className="h-3 w-3" />
              Start Sim
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Archive confirmation */}
      <DeleteConfirmModal
        agentName={agent.name}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        open={showDeleteModal}
      />
    </>
  );
}
